/**
 * loopSessionUtils.ts — Workflow loop session_id 推导（对齐 SDK WorkflowSessionIdPolicy）
 *
 * loop_session_id = `{workflow_id}-{task_key}`
 */
import type { LangchainAgentEvent } from '../../types/events';
import type { WorkflowContainerScope } from '../../types/workflowContainer';
import { resolveScopeKey } from './workflowContainerProjector';

function readString(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

/** 会在其内运行 Agent loop 的结构 open 事件 → 容器 scope */
const LOOP_TASK_OPEN_SCOPES: Partial<Record<string, WorkflowContainerScope>> = {
  'stage-start': 'stage',
  'parallel-item-start': 'item',
  'parallel-aggregate-start': 'aggregate',
  'route-branch-start': 'branch',
};

export function buildLoopSessionId(workflowId: string, taskKey: string): string {
  return `${workflowId}-${taskKey}`;
}

/** stage-start / parallel-item-start 等 open 事件预绑定 loop session。 */
export function deriveLoopSessionIdFromStructureOpen(
  event: LangchainAgentEvent,
): string | null {
  if (!LOOP_TASK_OPEN_SCOPES[event.event_type]) return null;

  const data = (event.data ?? {}) as Record<string, unknown>;
  const workflowId = readString(data, 'workflow_id');
  if (!workflowId) return null;

  const taskKey = resolveScopeKey(event.event_type, data);
  if (!taskKey || taskKey === 'unknown') return null;

  return buildLoopSessionId(workflowId, taskKey);
}

/** agent 事件缺 session_id 时，从 workflow_id (或 workflow_path 兜底) + scope key 推导。 */
export function deriveLoopSessionIdFromAgentEvent(
  event: LangchainAgentEvent,
): string | null {
  const data = (event.data ?? {}) as Record<string, unknown>;
  // 真捕获里 content 事件没有 workflow_id；但 SDK 容器投影 fixture 用 workflow_path。
  // 两者在根 workflow 上等价，所以允许 workflow_path 兜底（不破坏 D24，因为
  // workflow_id 永远优先；只是为了兼容用 workflow_path 命名 root 的 fixture）。
  const workflowId =
    readString(data, 'workflow_id') ?? readString(data, 'workflow_path');
  if (!workflowId) return null;

  const taskKey =
    readString(data, 'stage_key')
    ?? readString(data, 'item_key')
    ?? readString(data, 'branch_key')
    ?? readString(data, 'aggregate_key');
  if (!taskKey) return null;

  return buildLoopSessionId(workflowId, taskKey);
}

export function isLoopTaskStructureOpenEvent(eventType: string): boolean {
  return eventType in LOOP_TASK_OPEN_SCOPES;
}

export function loopTaskScopeForStructureOpen(
  eventType: string,
): WorkflowContainerScope | undefined {
  return LOOP_TASK_OPEN_SCOPES[eventType];
}
